import { filter, map, Observable, repeat, switchMap, take, takeWhile } from 'rxjs';
import {
  C_Ai_Assistant_Query_Status,
  GetWa_ReplyFromAiAssistantQuery,
  GetWa_ReplyFromAiAssistantQueryVariables,
  SendWa_QueryToAiAssistantMutation,
  SendWa_QueryToAiAssistantMutationVariables,
  GetWa_AiAssistantInitialisationDataQuery,
  GetWa_AiAssistantInitialisationDataQueryVariables,
} from '../../../../graphql/generatedModel';
import { gqlClient } from '../../../../graphql/graphqlRequest';
import { Pub, Service, Sub } from '../../../services/service.abstract';
import { aiAssistantPrompt, aiAssistantResponse, aiAssistantInfo } from './gql/aiAssistant.gql';

type Actions = 'prompt' | 'clear' | 'info';

class PubImpl extends Pub<Actions> {
  prompt(prompt: string): void {
    this.emit('prompt', prompt);
  }
  info(): void {
    this.emit('info', {});
  }
  clear(): void {
    this.emit('clear', {});
  }
}
class SubImpl extends Sub<Actions> {
  info(): Observable<AIAssistantInitialisationDataQueryRes> {
    return this.actionListener('info').pipe(
      switchMap(() => {
        const variables: GetWa_AiAssistantInitialisationDataQueryVariables = {
          appTimezoneOffset: new Date().getTimezoneOffset().toString(),
        };
        return gqlClient(
          aiAssistantInfo,
          variables,
        ) as Observable<GetWa_AiAssistantInitialisationDataQuery>;
      }),
      map((data: GetWa_AiAssistantInitialisationDataQuery) => {
        return data.wawiAssist
          ?.getWA_AIAssistantInitialisationData as AIAssistantInitialisationDataQueryRes;
      }),
    );
  }

  prompt(): Observable<AIAssistanRes> {
    return this.actionListener('prompt').pipe(
      switchMap(({ params }) => {
        const variables: SendWa_QueryToAiAssistantMutationVariables = {
          query: params,
          appTimezoneOffset: new Date().getTimezoneOffset().toString(),
        };
        return gqlClient(aiAssistantPrompt, variables);
      }),
      switchMap((value: SendWa_QueryToAiAssistantMutation) => {
        const maxPollingAttempts = 300;
        const jobId = value.wawiAssist?.sendWA_QueryToAIAssistant as string;
        const variables: GetWa_ReplyFromAiAssistantQueryVariables = {
          jobId,
        };
        return gqlClient(aiAssistantResponse, variables).pipe(
          map((value: GetWa_ReplyFromAiAssistantQuery) => {
            return value.wawiAssist?.getWA_ReplyFromAIAssistant;
          }),
          repeat<any>({ delay: 1000 }),
          takeWhile(
            ({
              status,
            }: NonNullable<
              GetWa_ReplyFromAiAssistantQuery['wawiAssist']
            >['getWA_ReplyFromAIAssistant']) => {
              return status === C_Ai_Assistant_Query_Status.AQS_LOADING;
            },
            true,
          ),
          filter(({ status }, index) => {
            const maxAttemptsReached = index + 1 > maxPollingAttempts;
            return (
              status === C_Ai_Assistant_Query_Status.AQS_SUCCESS ||
              status === C_Ai_Assistant_Query_Status.AQS_ERROR ||
              maxAttemptsReached
            );
          }),
          take(1),
          map((value: AIAssistanRes) => {
            return value;
          }),
        );
      }),
    );
  }
}

class AIAssistantService extends Service<Actions> {
  pub = new PubImpl(this.stream$);
  sub = new SubImpl(this.stream$);
}

export const aiAssistantService = new AIAssistantService();

export type AIAssistanRes = NonNullable<
  GetWa_ReplyFromAiAssistantQuery['wawiAssist']
>['getWA_ReplyFromAIAssistant'];

export type AIAssistantInitialisationDataQueryRes = NonNullable<
  GetWa_AiAssistantInitialisationDataQuery['wawiAssist']
>['getWA_AIAssistantInitialisationData'];
